import React, { useState } from 'react';
import { Modal } from './Modal';
import { ErrorAlert } from './ErrorAlert';
import { Archive, AlertTriangle, Loader2 } from 'lucide-react';
import { parseDatabaseError } from '@/lib/errors';

interface ArchiveRecordModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: 'Laptop' | 'Accessory' | 'Purchase';
  identifier: string;
  subIdentifier?: string;
  warnings?: string[];
  onConfirm: (reason: string) => Promise<void>;
  onSuccess?: () => void;
}

const ARCHIVE_CATEGORIES = [
  'Test record',
  'Duplicate display record',
  'Legacy record',
  'Other',
];

export const ArchiveRecordModal: React.FC<ArchiveRecordModalProps> = ({
  isOpen,
  onClose,
  entityType,
  identifier,
  subIdentifier,
  warnings = [],
  onConfirm,
  onSuccess,
}) => {
  const [category, setCategory] = useState<string>(ARCHIVE_CATEGORIES[0]);
  const [customExplanation, setCustomExplanation] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const finalReason = customExplanation.trim()
    ? `[${category}] ${customExplanation.trim()}`
    : `[${category}]`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customExplanation.trim()) {
      setErrorMsg('A detailed explanation is required to archive this record.');
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMsg(null);
      await onConfirm(finalReason);
      onClose();
      if (onSuccess) onSuccess();
    } catch (err: unknown) {
      setErrorMsg(parseDatabaseError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      title={`Archive / Hide ${entityType} — ${identifier}`}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {errorMsg && <ErrorAlert message={errorMsg} />}

        {/* Explanation Banner */}
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start space-x-2 text-xs text-amber-900">
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="font-bold">Owner Visibility Control</p>
            <p className="text-[11px] text-amber-800 leading-relaxed">
              Archiving hides this record from normal operational screens but preserves its transaction, accounting, and audit history.
            </p>
          </div>
        </div>

        {/* Target summary */}
        <div className="p-3 bg-brand-bg rounded-lg border border-brand-border text-xs">
          <span className="text-[10px] font-bold text-brand-muted uppercase tracking-wider block">Target Record</span>
          <p className="font-bold text-brand-black text-sm">{identifier}</p>
          {subIdentifier && <p className="text-xs text-brand-muted mt-0.5">{subIdentifier}</p>}
        </div>

        {warnings.length > 0 && (
          <div className="space-y-1.5">
            {warnings.map((w, idx) => (
              <div key={idx} className="p-2.5 bg-red-50 border border-red-200 rounded-md text-xs text-red-800 flex items-center space-x-2">
                <AlertTriangle className="w-3.5 h-3.5 text-brand-red shrink-0" />
                <span>{w}</span>
              </div>
            ))}
          </div>
        )}

        {/* Archive Category */}
        <div>
          <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
            Archive Category <span className="text-brand-red">*</span>
          </label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full px-3 py-2 bg-brand-bg border border-brand-border rounded-md text-xs font-semibold text-brand-black focus:ring-2 focus:ring-brand-green"
          >
            {ARCHIVE_CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>

        {/* Mandatory Detailed Explanation */}
        <div>
          <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
            Mandatory Explanation / Justification <span className="text-brand-red">*</span>
          </label>
          <textarea
            rows={2}
            placeholder="Explain why this record is being archived (e.g. created during development test)..."
            value={customExplanation}
            onChange={(e) => setCustomExplanation(e.target.value)}
            className="w-full px-3 py-2 bg-brand-bg border border-brand-border rounded-md text-xs text-brand-black focus:ring-2 focus:ring-brand-green"
            required
          />
          <p className="text-[10px] text-brand-muted mt-1">
            This justification will be permanently attached to the record and logged in the system audit trail.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end space-x-3 pt-3 border-t border-brand-border">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-xs font-medium text-brand-muted hover:text-brand-black transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || !customExplanation.trim()}
            className="inline-flex items-center space-x-1.5 px-4 py-2 bg-slate-800 text-white text-xs font-bold rounded-md hover:bg-slate-900 transition-colors disabled:opacity-50 shadow-sm"
          >
            {isSubmitting ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Archive className="w-3.5 h-3.5" />
            )}
            <span>Archive Record</span>
          </button>
        </div>
      </form>
    </Modal>
  );
};
